import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../store';
import { StructureViewMode } from '../../types';
import { urlParamReplicaId, urlProjectCategorizationStructure } from '../../Constants';
import { saveForm } from '../project/documents/sliceProjectDocumentDetails';
import { apiFetch } from '../../util/ProjectUtils';

export interface ICategorizationSuccess {
    docCount: number;
}

export function isICategorizationSuccess(object): object is ICategorizationSuccess {
    return object && typeof object === 'object' && 'docCount' in object;
}

export interface IProjectCategorizationStructure {
    IdDocIndex: string | null;
    Subject: string;
    IsSkeleton?: boolean;
}

export interface ICategorizationState {
    showDialog: boolean;
    mode?: StructureViewMode;
    structure: SlicePropsWithData<IProjectCategorizationStructure[]>;
    categorizationState: null | 'pending' | ICategorizationSuccess;
}

const initialState: ICategorizationState = {
    showDialog: false,
    mode: undefined,
    structure: {
        data: [],
        loading: false,
        error: null,
    },
    categorizationState: null,
};

const reducers = {
    showCategorizationDialog(state: ICategorizationState, action: IAction<StructureViewMode>) {
        state.mode = action.payload;
        state.showDialog = true;
    },
    hideCategorizationDialog(state: ICategorizationState) {
        state.mode = undefined;
        state.showDialog = false;
    },
    resetCategorizationState(state: ICategorizationState) {
        state.categorizationState = null;
    },
};

const thunks = {
    getProjectCategorizationStructure: async (config: {
        replicaId: string;
        structureName: StructureViewMode;
    }): Promise<IProjectCategorizationStructure[]> => {
        try {
            const { replicaId, structureName } = config;

            const url = urlProjectCategorizationStructure.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    viewName: structureName,
                },
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve((await response.json()) as IProjectCategorizationStructure[])
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    updateDocumentsCategorization: async (
        config: {
            replicaId: string;
            payload: {
                [key: string]: {
                    Category?: string[];
                    CategoryID?: string[];
                    Package?: string[];
                    PackageID?: string[];
                };
            };
            count: number;
        },
        thunkApi,
    ): Promise<void> => {
        try {
            const { replicaId, payload } = config;

            for (const [universalId, data] of Object.entries(payload)) {
                await thunkApi.dispatch(
                    saveForm({
                        replicaId,
                        universalId,
                        updatedForm: JSON.stringify(data),
                    }),
                );
            }

            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<ICategorizationState>) => {
        builder.addCase(
            getProjectCategorizationStructure.pending,
            (state: Draft<ICategorizationState>) => {
                state.structure.data = [];
                state.structure.loading = true;
                state.structure.error = null;
            },
        );
        builder.addCase(
            getProjectCategorizationStructure.fulfilled,
            (
                state: Draft<ICategorizationState>,
                action: IAction<IProjectCategorizationStructure[]>,
            ) => {
                state.structure.data = action.payload;
                state.structure.loading = false;
                state.structure.error = null;
            },
        );
        builder.addCase(
            getProjectCategorizationStructure.rejected,
            (state: Draft<ICategorizationState>, action: IAction<SerializedError>) => {
                state.structure.loading = false;
                state.structure.error = action.error;
                state.structure.data = [];
            },
        );

        builder.addCase(
            updateProjectCategorizationStructure.pending,
            (state: Draft<ICategorizationState>) => {
                state.categorizationState = 'pending';
            },
        );
        builder.addCase(
            updateProjectCategorizationStructure.fulfilled,
            (state: Draft<ICategorizationState>, action) => {
                state.categorizationState = {
                    docCount: action.meta.arg.count,
                } as ICategorizationSuccess;
            },
        );
        builder.addCase(
            updateProjectCategorizationStructure.rejected,
            (state: Draft<ICategorizationState>) => {
                state.categorizationState = null;
            },
        );
    },
};

export const getProjectCategorizationStructure = createAsyncThunk(
    'project/categorization/structure',
    thunks.getProjectCategorizationStructure,
);

export const updateProjectCategorizationStructure = createAsyncThunk(
    'project/categorization/structure/update',
    thunks.updateDocumentsCategorization,
);

export const categorizationSlice = createSlice({
    name: 'categorization',
    initialState,
    extraReducers: thunks._reducers,
    reducers,
});

export const { showCategorizationDialog, hideCategorizationDialog, resetCategorizationState } =
    categorizationSlice.actions;

export default categorizationSlice.reducer;
